import {
  ffLazyLoading,
  hasFeatureFlag,
  HTMLUtil,
  logger,
  Time,
  Util
} from 'yougile-util'
import {ChatMessage, ChatMessageJSON, Voting} from './chat-message'
import {newsCompanyId} from './constants'
import {
  DataObject,
  DataObjectId,
  DataObjectJSON,
  DataObjectProperties,
  isDataObjectId
} from './data-object'
import {GroupChat} from './group-chat'
import {HubJSON} from './hubs'
import {SystemMessageUtils} from './system-message-utils'

// Messages older than 100 days are always read
export const messageReadThreshold = 100 * 24 * 3600_000

export interface ChatJSON extends DataObjectJSON {
  messages: {[key: number]: ChatMessageJSON}
  lastSeenMessages: {[userId: string]: number}
  messagesCount?: number
}

export interface ChatType {
  isDirect: boolean
  isGroupChat: boolean
  isTaskChat: boolean
}

export class Chat extends DataObject {
  messagesCount = 0
  private lastSeenMessages: Map<DataObjectId, number> = new Map()
  public messages: Map<number, ChatMessage> = new Map()

  public updateMessagesCountIfNotLazy(): void {
    if (!this.isLazy) {
      this.messagesCount = Array.from(this.messages.values()).filter(
        m => !m.deleted
      ).length
    }
  }

  public getLastSeenMessage(userId: DataObjectId): number {
    const threshold = Time.now() - messageReadThreshold
    return Math.max(this.lastSeenMessages.get(userId) || 0, threshold)
  }

  public computeIsLazyChat(): boolean {
    const isLazyMode = hasFeatureFlag(ffLazyLoading)
    if (!isLazyMode) {
      return false
    }
    return [...this.messages.values()].some(
      SystemMessageUtils.isLazyPlaceholder
    )
  }

  private labels: Map<number, string> = new Map()

  constructor(companyId: DataObjectId) {
    super(companyId)
    this.dataType = 'Chat'
    this.isLazy = false
  }

  public override toJSON(): ChatJSON {
    const res = super.toJSON() as ChatJSON
    res.messages = {}
    this.messages.forEach((msg: ChatMessage, timestamp: number) => {
      res.messages[timestamp] = msg.toJSON()
    })
    res.messagesCount = this.messagesCount
    res.lastSeenMessages = Util.serializeStringMap(this.lastSeenMessages)

    return res
  }

  public override fromJSON(companyId: DataObjectId, data: ChatJSON): Chat {
    super.fromJSON(companyId, data)
    const isLazyMode = hasFeatureFlag(ffLazyLoading)
    const previousMessages = this.messages
    this.messages = new Map()
    if (isLazyMode) {
      for (const timestamp in previousMessages) {
        const message = previousMessages.get(+timestamp)
        if (message && SystemMessageUtils.isLazyPlaceholder(message)) {
          this.messages.set(+timestamp, message)
        }
      }
    }
    this.labels = new Map()
    let newMessagesCount = 0
    for (const ts in data.messages ?? {}) {
      if (!Object.prototype.hasOwnProperty.call(data.messages, ts)) {
        continue
      }
      if (+ts > 0) {
        const msgJson: ChatMessageJSON = data.messages[ts]
        const m = new ChatMessage(companyId, msgJson.from, '', '').fromJSON(
          companyId,
          msgJson
        )
        if (!m) {
          continue
        }
        if (
          isLazyMode &&
          !previousMessages.has(m.timestamp) &&
          !SystemMessageUtils.isLazyPlaceholder(m)
        ) {
          newMessagesCount++
        }
        this.messages.set(m.timestamp, m)
        if (m.label) {
          this.labels.set(m.timestamp, m.label)
        }
      } else {
        logger.warn('Incorrect data messages key')
      }
    }
    if (newMessagesCount > 0) {
      this.messagesCount += newMessagesCount
    }
    this.messagesCount =
      data.messagesCount ?? Math.max(this.messagesCount, this.messages.size)
    this.lastSeenMessages = Util.deserializeStringMap(
      data.lastSeenMessages ?? {}
    ) as Map<DataObjectId, number>
    this.updateMessagesCountIfNotLazy()
    return this
  }

  static lastMessageForJson(chat: ChatJSON | undefined): number | undefined {
    if (!chat?.messages) {
      return undefined
    }
    const times = Object.keys(chat.messages)
    return times.length > 0 ? +times[times.length - 1] : undefined
  }

  static lastNotSystemMessageForJson(
    chat: ChatJSON | undefined
  ): number | undefined {
    if (!chat?.messages) {
      return undefined
    }
    const messageTimes = Object.keys(chat.messages ?? {})
    if (!messageTimes.length) {
      return undefined
    }
    const maxIx = messageTimes.length - 1
    const minIx = Math.max(0, maxIx - 10)

    for (let i = maxIx; i >= minIx; i--) {
      const time = +messageTimes[i]
      const m = chat.messages[time]
      if (!m.deleted && !SystemMessageUtils.isSystemMessage(m)) {
        return time
      }
    }
    return undefined
  }

  static newMessagesForJson(
    chat: ChatJSON,
    userId: DataObjectId,
    startFrom: number,
    includeSystem: boolean
  ): ChatMessageJSON[] {
    if (!userId) {
      return []
    }
    const messageTimes = Object.keys(chat.messages ?? {})
    if (!messageTimes.length) {
      return []
    }

    const threshold = Math.max(startFrom, Time.now() - messageReadThreshold)
    const time = Math.max(chat.lastSeenMessages[userId] ?? 0, threshold)

    if (!time) {
      return messageTimes.map(t => chat.messages[+t])
    }
    const result: ChatMessageJSON[] = []

    const maxIx = messageTimes.length - 1
    const minIx = Math.max(0, maxIx - 100)

    for (let i = maxIx; i >= minIx; i--) {
      const m = chat.messages[+messageTimes[i]]
      if (m.timestamp < time) {
        break
      }
      if (
        !m.deleted &&
        m.from !== userId &&
        (includeSystem
          ? !SystemMessageUtils.isSystemMessage(m) ||
            SystemMessageUtils.isKnownSystemMessage(m)
          : !SystemMessageUtils.isSystemMessage(m))
      ) {
        result.push(m)
      }
    }

    return result
  }

  public firstMessage(): ChatMessage | undefined {
    return this.messages.size > 0 ? [...this.messages.values()][0] : undefined
  }

  public lastMessage(options?: {
    userId?: DataObjectId
    includeSystem?: boolean
    ignoreReadSystemMessages?: boolean
    forUser?: DataObjectId
  }): ChatMessage | undefined {
    if (this.messages.size === 0) {
      return undefined
    }

    const currentUserTimestamp = options?.forUser
      ? this.lastSeenMessages.get(options.forUser)
      : undefined

    const userId = options?.userId
    const includeSystem = options?.includeSystem ?? true
    const ignoreReadSystemMessages = options?.ignoreReadSystemMessages ?? false
    let maxTime = 0
    this.messages.forEach(m => {
      const isRead = currentUserTimestamp
        ? m.timestamp <= currentUserTimestamp
        : true
      if (
        (!userId || m.from === userId) &&
        m.timestamp > maxTime &&
        !m.deleted &&
        (includeSystem
          ? !SystemMessageUtils.isSystemMessage(m) ||
            (SystemMessageUtils.isKnownSystemMessage(m) &&
              (!ignoreReadSystemMessages || !isRead))
          : !SystemMessageUtils.isSystemMessage(m))
      ) {
        maxTime = m.timestamp
      }
    })
    return maxTime ? this.messages.get(maxTime) : undefined
  }

  public newMessagesFor(
    userId: DataObjectId,
    startFrom = 0,
    includeSystem = true,
    forceStartFrom = false,
    filter: (m: ChatMessage) => boolean = () => true
  ): number {
    if (!userId) {
      return 0
    }
    if (this.messages.size === 0) {
      return 0
    }

    const threshold = Math.max(startFrom, Time.now() - messageReadThreshold)
    const time = forceStartFrom
      ? startFrom
      : Math.max(this.lastSeenMessages.get(userId) || 0, threshold)

    let count = 0
    if (!time) {
      return this.messages.size
    }
    this.messages.forEach(m => {
      if (
        time < m.timestamp &&
        !m.deleted &&
        m.from !== userId &&
        (includeSystem
          ? !SystemMessageUtils.isSystemMessage(m) ||
            SystemMessageUtils.isKnownSystemMessage(m)
          : !SystemMessageUtils.isSystemMessage(m)) &&
        filter(m)
      ) {
        count++
      }
    })

    return count
  }

  public getNewMessages(
    userId: DataObjectId,
    startFrom = 0,
    includeSystem = true
  ): ChatMessage[] {
    const startTime = Math.max(startFrom, Time.now() - messageReadThreshold)
    if (!userId) {
      return []
    }
    if (this.messages.size === 0) {
      return []
    }
    const time = Math.max(this.lastSeenMessages.get(userId) || 0, startTime)
    if (!time) {
      return [...this.messages.values()]
    }
    const res: ChatMessage[] = []
    this.messages.forEach(m => {
      if (
        time < m.timestamp &&
        !m.deleted &&
        (includeSystem
          ? !SystemMessageUtils.isSystemMessage(m) ||
            SystemMessageUtils.isKnownSystemMessage(m)
          : !SystemMessageUtils.isSystemMessage(m))
      ) {
        res.push(m)
      }
    })
    return res
  }

  public setLastSeen(userId: DataObjectId, timestamp?: number): boolean {
    Util.assert(!!userId, 'User path must be defined.')
    if (this.companyId === newsCompanyId) {
      return false
    }

    if (this.messages.size === 0 || typeof timestamp === 'undefined') {
      if (this.lastSeenMessages.get(userId) !== undefined) {
        this.lastSeenMessages.delete(userId)
        return true
      }
    } else {
      if (this.lastSeenMessages.get(userId) !== timestamp) {
        this.lastSeenMessages.set(userId, timestamp)
        return true
      }
    }
    return false
  }

  public read(userId: DataObjectId, limitTime = -1): boolean {
    Util.assert(!!userId, 'User path must be defined.')
    if (this.messages.size === 0 || this.companyId === newsCompanyId) {
      return false
    }
    const time = this.lastSeenMessages.get(userId)
    let maxTime = Math.max(...this.messages.keys())
    if (limitTime > 0 && limitTime < maxTime) {
      maxTime = limitTime
    }
    if (!time || time < maxTime) {
      this.lastSeenMessages.set(userId, maxTime)
      return true
    } else {
      return false
    }
  }

  public addMessage(
    fromId: DataObjectId,
    {
      text,
      textHtml,
      messageId,
      voting,
      label = ''
    }: {
      text?: string
      textHtml?: string
      voting?: Voting
      label?: string
      messageId?: DataObjectId
    },
    {
      timestamp,
      properties,
      noChangeSeenMessages
    }: {
      timestamp?: number
      properties?: DataObjectProperties
      noChangeSeenMessages?: boolean
    } = {}
  ): ChatMessage {
    Util.assert(fromId && Util.isIdOrUUID(fromId), 'fromId must be UUID')
    Util.assert(!!text || !!textHtml, 'Either text or textHtml must be set')
    if (textHtml) {
      text = text || HTMLUtil.messageTextHtmlToMessageText(textHtml)
    }
    Util.assert(!!text, 'Text must be not empty.')

    const m = new ChatMessage(
      this.companyId,
      fromId,
      text ?? '',
      label,
      properties
    )

    if (messageId) {
      m.id = messageId
    }

    if (textHtml && HTMLUtil.hasComplexFormatting(textHtml)) {
      m.setTextHtml(textHtml)
    }
    if (timestamp) {
      m.timestamp = timestamp
    }

    if (voting) {
      m.properties ??= {}
      m.properties.voting = voting
    }

    // prevent message timestamp collisions
    let count = 0
    while (this.messages.has(m.timestamp) && count < 30) {
      m.timestamp++
      count++
    }

    this.messages.set(m.timestamp, m)
    if (this.messagesCount) {
      this.messagesCount++
    }
    if (!noChangeSeenMessages && this.companyId !== newsCompanyId) {
      this.lastSeenMessages.set(fromId, m.timestamp)
    }
    this.updateMessagesCountIfNotLazy()
    return m
  }

  public addLabel(timestamp: number, label: string): boolean {
    label = label.trim()
    const msg = this.messages.get(timestamp)
    if (!msg) {
      return false
    }
    if (msg.label === label) {
      return false
    } else if (label === '') {
      this.labels.delete(timestamp)
      msg.label = ''
      return true
    } else {
      this.labels.set(timestamp, label)
      msg.label = label
      return true
    }
  }

  public getLabels(): Array<{label: string; timestamp: number}> {
    return [...this.labels]
      .map((v: [number, string]) => ({
        label: v[1],
        timestamp: v[0]
      }))
      .filter(p => {
        const msg = this.messages.get(p.timestamp)
        return msg && !msg.deleted
      })
  }

  get length(): number {
    return this.messagesCount ?? this.messages.size
  }

  static getMessageCountForJson(chat: ChatJSON | undefined): number {
    if (!chat?.messages) {
      return 0
    }
    const times = Object.keys(chat.messages)

    // if there are many messages, then using faster version
    // because in this case it is not so important to have
    // super precise count for users
    if (times.length > 20) {
      return times.length
    }

    let count = 0
    for (const time of times) {
      const msg = chat.messages[+time]
      if (!msg?.deleted && !SystemMessageUtils.isSystemMessage(msg)) {
        count++
      }
    }
    return count
  }

  public getMessages(): ChatMessage[] {
    return [...this.messages.values()].filter(
      m => !!m && !m.deleted && !SystemMessageUtils.isSystemMessage(m)
    )
  }

  public getMessagesCount(): number {
    return this.getMessages().length
  }

  public findMessage(id: DataObjectId): ChatMessage | undefined {
    return [...this.messages.values()].find((m: ChatMessage) => m.id === id)
  }

  public static isDirectChat(hub: HubJSON): boolean {
    if (!hub) {
      return false
    }
    if (
      !hub.title.includes('|') ||
      hub.title.split('|').find((s: string) => !Util.isIdOrUUID(s))
    ) {
      return false
    }
    const userIds = hub.title.split('|') as DataObjectId[]
    return (
      userIds.length === 2 &&
      Util.isIdOrUUID(userIds[0]) &&
      Util.isIdOrUUID(userIds[1])
    )
  }

  public static getPartnerIdFromHub(
    userId: DataObjectId,
    chat: HubJSON
  ): DataObjectId | undefined {
    if (!chat?.title.includes('|')) {
      return undefined
    }
    const [u1, u2] = chat.title.split('|')

    if (isDataObjectId(u1) && isDataObjectId(u2)) {
      return u1 === userId ? u2 : u2 === userId ? u1 : undefined
    }
    return undefined
  }

  public static chatType(hub: HubJSON): ChatType {
    const isDirect = Chat.isDirectChat(hub)
    const isGroupChat = !!hub && GroupChat.isGroupChat(hub)

    return {
      isDirect,
      isGroupChat,
      isTaskChat: !isDirect && !isGroupChat
    }
  }
}
